<script setup lang="ts">
const props = defineProps<{
  flyer: {
    id: number
    slug?: string
    store: {
      slug: string
      store_name: string
      image_url: string
      price_weight: number
      show_name_instead_of_logo?: boolean | number
      text_color?: string
    }
    flyer_thumb: string
    end_date: string
    start_date: string
    end_human_formatted: string
    start_human_formatted: string
    review_status: number
  }
}>()

const modalStore = useModalStore()
const userStore = useUserStore()
const config = useRuntimeConfig()
const toast = useToast()

async function saveStore(storeSlug: string, action: 'add' | 'remove') {
  if (userStore.user) {
    try {
      const response = await $fetch<{ success: number, stores: string[] }>(`${config.public.apiUrl}/user/save-store`, {
        method: 'POST',
        body: { slug: storeSlug, action },
        headers: {
          Authorization: `Bearer ${userStore.access_token}`,
        },
      })

      if (response.success === 1) {
        // Update user stores in the store
        userStore.user.stores = response.stores

        if (action === 'add') {
          toast.add({
            title: 'Commerce ajouté à vos favoris',
            color: 'success',
            icon: 'i-lucide-check-circle',
          })
        }
        else {
          toast.add({
            title: 'Commerce retiré de vos favoris',
            color: 'success',
            icon: 'i-lucide-check-circle',
          })
        }
      }
    }
    catch (err) {
      console.error('Error saving store:', err)
      toast.add({
        title: 'Erreur',
        description: 'Impossible de modifier vos commerces favoris.',
        color: 'error',
        icon: 'i-lucide-alert-circle',
      })
    }
  }
  else {
    toast.add({
      title: 'Connexion requise',
      description: 'Un compte Glouton est requis pour cette action.',
      color: 'error',
      icon: 'i-lucide-alert-circle',
    })
  }
}

// Check if store is in user's favorites
const isStoreInFavorites = computed(() => {
  return userStore.user?.stores?.includes(props.flyer.store.slug) || false
})
</script>

<template>
  <div class="flex flex-col h-full text-center bg-white rounded-lg shadow gap-2 hover:shadow-lg">
    <dl class="w-full p-0 shrink-0">
      <router-link class="relative rounded-t-lg overflow-hidden block aspect-square group" as="a" :to="$localePath({ name: 'FlyerPage', params: { id: flyer.id, slug: flyer.slug } })">
        <img
          class="block w-full transition-transform duration-300 ease-in-out transform group-hover:scale-103"
          :src="flyer.flyer_thumb"
          :alt="flyer.store.store_name"
          width="360"
          height="360"
          loading="lazy"
          decoding="async"
        >
      </router-link>
    </dl>
    <div class="min-h-4 md:min-h-6 mb-0 relative shrink-0 flex items-center justify-center px-2">
      <img
        v-if="!flyer.store.show_name_instead_of_logo"
        class="h-4 md:h-6 mx-auto"
        :src="flyer.store.image_url"
        :alt="flyer.store.store_name"
      >
      <span
        v-else
        class="font-bold text-[13px] leading-tight text-center w-full"
        :style="{ color: flyer.store.text_color || '#000000' }"
      >
        {{ flyer.store.store_name }}
      </span>
    </div>
    <div class="flex-1 flex justify-center items-center text-poivre-600 text-xs min-h-0">
      <div v-if="Date.parse(flyer.end_date) - Date.now() < 0" class="">
        <span class="w-full">Terminé {{ flyer.end_human_formatted }}</span>
      </div>
      <div v-else-if="Date.parse(flyer.start_date) > Date.now()" class="">
        <span class="w-full">Commence {{ flyer.start_human_formatted }}</span>
      </div>
      <div v-else class="">
        <span class="w-full">Se termine {{ flyer.end_human_formatted }}</span>
      </div>
    </div>
    <div class="flex justify-between border-t gap-0.5 border-gray-200 p-1 shrink-0">
      <div v-if="flyer.store.price_weight === 1 && flyer.review_status >= 2" class="flex justify-center w-full items-center text-green-500 text-xs md:text-sm font-medium">
        <UIcon name="i-mdi-check-circle-outline" class="mr-1 size-4 md:size-5" /> <span @click="modalStore.openScoreHelpModal">Analysé par Glouton</span>
      </div>
      <div v-else-if="flyer.store.price_weight === 1 && flyer.review_status < 2" class="flex justify-center w-full items-center">
        <span class="inline-flex text-yellow-500 text-xs md:text-sm items-center justify-center cursor-pointer"><UIcon name="i-mdi-update" class="mr-1 text-sm hidden md:inline" /> <span>Analyse bientôt disponible</span></span>
      </div>
      <div v-else class="flex justify-center w-full items-center" />

      <UDropdownMenu
        :items="[
          isStoreInFavorites
            ? {
              label: `Retirer de mes commerces favoris`,
              icon: 'i-mdi-store-remove',
              onSelect: () => saveStore(flyer.store.slug, 'remove'),
            }
            : {
              label: `Ajouter à mes commerces favoris`,
              icon: 'i-mdi-store-plus',
              onSelect: () => saveStore(flyer.store.slug, 'add'),
            },
        ]"
        :content="{ align: 'end', sideOffset: 1 }"
      >
        <UButton
          color="secondary"
          variant="ghost"
          icon="i-mdi-cog"
          size="xs"
        />
      </UDropdownMenu>
    </div>
  </div>
</template>
